import { Button, Dialog, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import { convertISODateTimeShort } from "@entrilia/shared/utilities/dateUtils";
import type { ObserverIssueInfo } from "../../../api/observerTypes";

const MAX_REASON_LENGTH = 10000;

interface Props {
  issue: ObserverIssueInfo;
  saving: boolean;
  onSave: (issue: ObserverIssueInfo, reason: string) => void;
  onClose: () => void;
}

const ObserverAcknowledgementDialog = ({ issue, saving, onSave, onClose }: Props) => {
  const isAcknowledged = issue.status === "Acknowledged";
  const initialReason = isAcknowledged ? (issue.acknowledgementReason ?? "") : "";
  const [reason, setReason] = useState(initialReason);

  const trimmedReason = reason.trim();
  const isUnchanged = isAcknowledged && trimmedReason === initialReason.trim();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isAcknowledged ? "Acknowledgement" : "Acknowledge issue"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {isAcknowledged && (
            <Typography variant="caption" color="textSecondary">
              {[
                issue.statusChangedBy && `Acknowledged by ${issue.statusChangedBy}`,
                issue.statusChangedAtUtc && convertISODateTimeShort(issue.statusChangedAtUtc),
                issue.assignedToUserName && `Assignee: ${issue.assignedToUserName}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
          <TextField
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline
            minRows={8}
            maxRows={20}
            fullWidth
            autoFocus
            helperText={`${reason.length.toLocaleString()} / ${MAX_REASON_LENGTH.toLocaleString()} characters · Saving makes you the assignee.`}
            slotProps={{ htmlInput: { maxLength: MAX_REASON_LENGTH } }}
          />
        </Stack>
      </DialogContent>
      <DialogButtons>
        <Button variant="text" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={saving}
          disabled={!trimmedReason || isUnchanged}
          onClick={() => onSave(issue, trimmedReason)}
        >
          Save
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default ObserverAcknowledgementDialog;
